import type { LanterneEffect } from '../../../data/full/veilleur';
import { rotate } from '../../../shared/angle';
import { dot, length, sub } from '../../../shared/vec';
import { keepWhere } from '../../effects';
import type { SkillModule } from '../../skills/types';
import type { PlayerState, PropState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { flightTicks, flyHome, preyOf, sting } from '../attacks/swarm';
import { hourOf, nightLit } from '../hour';
import { flyBack, ownerOf } from '../../props';

export const LANTERN_FIREFLY = 'lanternFirefly';

const DEGREES = Math.PI / 180;

export const lanterne: SkillModule<LanterneEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state } = ctx;
    const count = burstSize(ctx, player, effect);
    const half = (effect.fanDegrees * DEGREES) / 2;
    const edge = rotate({ x: 1, y: 0 }, half).x;
    const prey = preyOf(state.enemies, player, effect.reach).filter((enemy) => {
      const away = sub(enemy, player);
      return dot(away, player.aim) >= edge * length(away);
    });
    for (let rank = 0; rank < count; rank++) {
      const target = prey[rank % Math.max(1, prey.length)];
      const turn = count === 1 ? 0 : -half + (2 * half * rank) / (count - 1);
      const way = rotate(player.aim, turn);
      const firefly: PropState = {
        id: state.nextEntityId,
        kind: LANTERN_FIREFLY,
        playerId: player.id,
        phase: 'flying',
        x: player.x,
        y: player.y,
        prevX: player.x,
        prevY: player.y,
        vx: way.x * effect.speed,
        vy: way.y * effect.speed,
        damage: effect.damage * power,
        ticksLeft:
          target === undefined
            ? Math.ceil(effect.reach / effect.speed)
            : flightTicks(effect.reach, effect.speed),
        hitsLeft: 1,
        hitIds: [],
        rank,
      };
      if (target !== undefined) {
        firefly.targetId = target.id;
      }
      state.nextEntityId += 1;
      (state.props ??= []).push(firefly);
    }
  },

  update(ctx) {
    const props = ctx.state.props;
    if (props === undefined) {
      return;
    }
    let home = false;
    for (const prop of props) {
      if (prop.kind !== LANTERN_FIREFLY) {
        continue;
      }
      const owner = ownerOf(ctx.state, prop);
      const effect = lanterneOf(ctx, owner);
      if (prop.phase === 'returning') {
        flyBack(prop, owner, effect.returnSpeed);
      } else if (prop.targetId !== undefined) {
        sting(ctx, prop, effect);
      } else {
        drift(prop);
      }
      home ||= prop.phase === 'held';
    }
    if (home) {
      keepWhere(props, (prop) => prop.kind !== LANTERN_FIREFLY || prop.phase !== 'held');
      if (props.length === 0) {
        delete ctx.state.props;
      }
    }
  },
};

function burstSize(
  ctx: Pick<StepContext, 'set' | 'state'>,
  player: PlayerState,
  effect: LanterneEffect,
): number {
  return statValue(player, 'skillCountAdd', effect.fireflies) + nightLit(effect.night, hourOf(ctx));
}

function lanterneOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): LanterneEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'lanterne') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no lantern`);
  }
  return effect;
}

function drift(prop: PropState): void {
  prop.x += prop.vx;
  prop.y += prop.vy;
  prop.ticksLeft -= 1;
  if (prop.ticksLeft <= 0) {
    flyHome(prop);
  }
}
